import { useEffect, useState } from 'react';
import { Alert, Button, ToggleButton, ToggleButtonGroup, Avatar, Box, Dialog, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import { LeaderboardIcon } from './PixelIcons';
import { getLeaderboard } from '../utils/api';

const leaderRainbow = 'conic-gradient(#ff2d95, #ff9a3c, #ffe56b, #69e5a3, #00e5ff, #ad86ff, #ff2d95)';

const LeaderboardAvatar = ({ friend, isLeader, period }) => (
  <Box sx={{
    position: 'relative',
    width: 40,
    height: 40,
    p: '3px',
    borderRadius: '11px',
    ...(isLeader && {
      '&::before': {
        content: '""', position: 'absolute', inset: -3, borderRadius: '14px',
        background: leaderRainbow, filter: 'blur(7px)',
        opacity: 0.75, animation: 'leader-rainbow 6s linear infinite',
      },
      '@keyframes leader-rainbow': {
        from: { transform: 'rotate(0deg)' },
        to: { transform: 'rotate(360deg)' },
      },
      '@media (prefers-reduced-motion: reduce)': { '&::before': { animation: 'none', opacity: 0.7 } },
    }),
  }}>
    {isLeader && <Box aria-hidden="true" sx={{
      position: 'absolute', inset: 0, borderRadius: '11px', overflow: 'hidden',
      '&::before': {
        content: '""', position: 'absolute', inset: -10,
        background: leaderRainbow, animation: 'leader-rainbow 6s linear infinite',
      },
      '@media (prefers-reduced-motion: reduce)': { '&::before': { animation: 'none' } },
    }} />}
    <Avatar
      alt={isLeader ? `${friend.username}, ${period === 'daily' ? "today's leader" : 'total points leader'}` : `${friend.username} profile picture`}
      src={friend.profile_image_url || '/dope_logo_192.png'}
      variant="rounded"
      sx={{ position: 'relative', width: 34, height: 34, borderRadius: '8px', bgcolor: 'background.paper' }}
    >
      <Box component="img" src="/dope_logo_192.png" alt="" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    </Avatar>
  </Box>
);

const FriendsLeaderboard = ({ open, onClose }) => {
  const [period, setPeriod] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    let requestNumber = 0;
    let midnightTimer;
    setData(null);
    setError('');
    setLoading(true);
    const load = async () => {
      const current = ++requestNumber;
      try {
        const result = await getLeaderboard(period);
        if (!alive || current !== requestNumber) return;
        setData(result);
        setError('');
        window.clearTimeout(midnightTimer);
        const endsAt = Date.parse(result?.day_ends_at);
        if (Number.isFinite(endsAt)) {
          midnightTimer = window.setTimeout(load, Math.max(1000, endsAt - Date.now() + 100));
        }
      } catch (err) {
        if (alive && current === requestNumber) setError('Unable to load scores. Please try again.');
      } finally {
        if (alive && current === requestNumber) setLoading(false);
      }
    };
    const refresh = () => { if (document.visibilityState === 'visible') load(); };
    load();
    const interval = window.setInterval(refresh, 30000);
    window.addEventListener('profileUpdate', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      alive = false;
      window.clearInterval(interval);
      window.clearTimeout(midnightTimer);
      window.removeEventListener('profileUpdate', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [open, period, retry]);

  return (
    <Dialog
      id="friends-leaderboard"
      open={open}
      onClose={onClose}
      aria-labelledby="friends-leaderboard-title"
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: theme => ({
        m: 2, width: 'calc(100% - 32px)', border: '1px solid', borderColor: 'primary.main', borderRadius: '20px',
        background: `radial-gradient(ellipse at top, ${alpha(theme.palette.secondary.main, 0.12)}, transparent 65%), ${theme.palette.background.paper}`,
        boxShadow: `0 16px 64px ${alpha(theme.palette.common.black, 0.7)}, 0 0 28px ${alpha(theme.palette.primary.main, 0.18)}`,
      }) }}
    >
      <DialogTitle id="friends-leaderboard-header" component="div" sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 1 }}>
        <Typography id="friends-leaderboard-title" component="h2" sx={theme => ({
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          fontSize: { xs: 11, sm: 16 }, lineHeight: 1.8, color: 'secondary.main', pr: 3,
          textShadow: `0 0 16px ${alpha(theme.palette.secondary.main, 0.35)}`,
        })}>
          <LeaderboardIcon aria-hidden="true" focusable="false" style={{ width: 22, height: 22, flexShrink: 0 }} />
          Leaderboard
        </Typography>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_event, value) => { if (value) setPeriod(value); }}
          aria-label="Leaderboard period"
          size="small"
          sx={{ display: 'flex', justifyContent: 'center', mt: 1.5, '& .MuiToggleButton-root': { fontSize: { xs: 8, sm: 10 }, px: 2, py: 1, m: 0, color: 'primary.main', borderColor: 'primary.main', '&.Mui-selected': { bgcolor: 'primary.seeThrough', color: 'text.primary' } } }}
        >
          <ToggleButton value="daily">Daily</ToggleButton>
          <ToggleButton value="total">Total</ToggleButton>
        </ToggleButtonGroup>
        <IconButton aria-label="Close leaderboard" onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12, color: 'primary.main' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 2, maxHeight: '65vh' }}>
        {loading && <Typography component="div" role="status" sx={{ py: 4, textAlign: 'center', fontSize: { xs: 9, sm: 11 } }}>Loading scores…</Typography>}
        {error && <Alert severity="error" action={<Button onClick={() => setRetry(value => value + 1)}>Retry</Button>}>{error}</Alert>}
        {data?.period === period && !loading && <Table aria-label={`${period === 'daily' ? 'Daily' : 'Total'} points leaderboard`} size="small" sx={{
          tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: '0 6px',
          '& th, & td': { border: 0, px: 0, py: 1, fontSize: { xs: 10, sm: 12 } },
        }}>
          <TableBody>
            {data.entries.map(friend => (
              <TableRow key={friend.id} sx={theme => ({
                background: friend.is_leader
                  ? `linear-gradient(90deg, ${alpha(theme.palette.secondary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.06)})`
                  : friend.is_you ? theme.palette.action.hover : 'transparent',
              })}>
                <TableCell sx={{ width: 56, borderRadius: '12px 0 0 12px', '&&': { pl: 0.5 } }}>
                  <LeaderboardAvatar friend={friend} isLeader={friend.is_leader} period={period} />
                </TableCell>
                <TableCell component="th" scope="row" sx={{ color: friend.is_leader ? 'secondary.main' : 'primary.main', overflowWrap: 'anywhere' }}>
                  {friend.is_you ? 'You' : friend.username}
                </TableCell>
                <TableCell align="right" aria-label={`${friend.score.toLocaleString()} ${period === 'daily' ? 'points today' : 'total points'}`} sx={{
                  width: { xs: 96, sm: 110 }, '&&': { pr: 1 },
                  color: friend.is_leader ? 'secondary.main' : 'text.primary', fontVariantNumeric: 'tabular-nums', borderRadius: '0 12px 12px 0',
                }}>
                  {String(friend.score).padStart(6, '0')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>}
      </DialogContent>
    </Dialog>
  );
};

export default FriendsLeaderboard;
